import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DsFormsModule } from '@bmw-ds/components';

import { DealerProvider } from '../../../../auth/dealer/dealer.provider';
import { CustomerVehicle } from '../../../customer/model/customer-vehicle.model';
import { useI18n } from '../../../i18n/services/i18n.service';
import { VerifyChoiceModalWrapper } from '../../../modal/verify-choice-modal/verify-choice-modal.wrapper';
import { VehicleInfoComponent } from '../vehicle-info/vehicle-info.component';

@Component({
  selector: 'cdf-edit-license-plate-modal',
  imports: [VerifyChoiceModalWrapper, FormsModule, DsFormsModule, VehicleInfoComponent],
  template: `
    <cdf-verify-choice-modal-wrapper
      class="edit-license-plate-modal"
      [open]="true"
      size="lg"
      [headingText]="i18n('vehicles.licensePlate.editModal.heading')"
      [bodyText]="editLicensePlateModalBody"
      [verifyButtonText]="verifyButtonText()"
      [buttonLoading]="loading()"
      [disableVerifyButton]="!isRemovingLicensePlate() && isLicensePlateEmpty()"
      [isDestructiveAction]="isRemovingLicensePlate()"
      (verify)="confirmEdit.emit(licensePlate().trim())"
      (closeEvent)="hideModal.emit()"
    />
    <ng-template #editLicensePlateModalBody>
      <p class="text-regular mb-2x">{{ i18n('vehicles.licensePlate.editModal.vehicle') }}</p>
      <cdf-vehicle-info [vehicle]="vehicle()" [activeCountry]="dealerProvider.activeCountry()" smallImage />
      <p class="text-regular mt-3x mb-2x">{{ i18n('vehicles.licensePlate.editModal.prompt') }}</p>
      <ds-form-field>
        <label ds-label for="cdf-edit-license-plate-modal-input">{{
          i18n('vehicles.licensePlate.editModal.inputLabel')
        }}</label>
        <input
          ds-input
          id="cdf-edit-license-plate-modal-input"
          maxlength="12"
          [(ngModel)]="licensePlate"
          name="registrationNumber"
        />
      </ds-form-field>
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditLicensePlateModalWrapper {
  protected readonly i18n = useI18n();
  protected readonly dealerProvider = inject(DealerProvider);

  readonly vehicle = input.required<CustomerVehicle>();
  readonly loading = input(false);

  protected readonly licensePlate = linkedSignal(() => this.vehicle().licensePlate ?? '');

  private readonly hasExistingLicensePlate = computed(() => !!this.vehicle().licensePlate?.trim());
  protected readonly isLicensePlateEmpty = computed(() => !this.licensePlate().trim());
  protected readonly isRemovingLicensePlate = computed(
    () => this.hasExistingLicensePlate() && this.isLicensePlateEmpty(),
  );

  protected readonly verifyButtonText = computed(() =>
    this.isRemovingLicensePlate()
      ? this.i18n('vehicles.licensePlate.editModal.removeButton')
      : this.i18n('vehicles.licensePlate.editModal.saveButton'),
  );

  readonly confirmEdit = output<string>();
  readonly hideModal = output();
}
